"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MetaPool = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const currency_1 = require("@zenlink-interface/currency");
const math_1 = require("@zenlink-interface/math");
const jsbi_1 = __importDefault(require("jsbi"));
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const util_1 = require("../../util");
const BasePool_1 = require("./BasePool");
const BASE_VIRTUAL_PRICE_PRECISION = jsbi_1.default.BigInt(1e18);
const FEE_DENOMINATOR = jsbi_1.default.BigInt(1e10);
function getTokenIndexsAndBalances(baseSwap, metaSwap, token0, token1) {
    let token0Index = 0;
    let balance0 = bignumber_1.BigNumber.from(0);
    let token1Index = 0;
    let balance1 = bignumber_1.BigNumber.from(0);
    if (baseSwap.involvesToken(token0)) {
        const baseToken0Index = baseSwap.getTokenIndex(token0);
        token0Index = metaSwap.pooledTokens.length - 1 + baseToken0Index;
        balance0 = bignumber_1.BigNumber.from(baseSwap.balances[baseToken0Index].quotient.toString());
    }
    if (baseSwap.involvesToken(token1)) {
        const baseToken1Index = baseSwap.getTokenIndex(token1);
        token1Index = metaSwap.pooledTokens.length - 1 + baseToken1Index;
        balance1 = bignumber_1.BigNumber.from(baseSwap.balances[baseToken1Index].quotient.toString());
    }
    if (metaSwap.involvesToken(token0)) {
        token0Index = metaSwap.getTokenIndex(token0);
        balance0 = bignumber_1.BigNumber.from(metaSwap.balances[token0Index].quotient.toString());
    }
    if (metaSwap.involvesToken(token1)) {
        token1Index = metaSwap.getTokenIndex(token1);
        balance1 = bignumber_1.BigNumber.from(metaSwap.balances[token1Index].quotient.toString());
    }
    return [[token0Index, token1Index], [balance0, balance1]];
}
class MetaPool extends BasePool_1.BasePool {
    constructor(baseSwap, metaSwap, token0, token1, fee) {
        (0, tiny_invariant_1.default)(metaSwap.pooledTokens.some(token => token.address.toLowerCase() === baseSwap.liquidityToken.address.toLowerCase()), 'MetaPool: PooledTokens not including baseLiquidityToken');
        const [[token0Index, token1Index], [balance0, balance1],] = getTokenIndexsAndBalances(baseSwap, metaSwap, token0, token1);
        super(metaSwap.contractAddress, token0, token1, fee, balance0, balance1);
        this.baseSwap = baseSwap;
        this.metaSwap = metaSwap;
        this._token0 = token0;
        this._token1 = token1;
        this.token0Index = token0Index;
        this.token1Index = token1Index;
    }
    updateSwap(baseSwap, metaSwap) {
        (0, tiny_invariant_1.default)(baseSwap.contractAddress === this.baseSwap.contractAddress, 'MetaPool: Different basePool!');
        (0, tiny_invariant_1.default)(metaSwap.contractAddress === this.metaSwap.contractAddress, 'MetaPool: Different metaPool!');
        const [_, [res0, res1],] = getTokenIndexsAndBalances(baseSwap, metaSwap, this._token0, this._token1);
        this.baseSwap = baseSwap;
        this.metaSwap = metaSwap;
        if (!res0.eq(this.reserve0) || !res1.eq(this.reserve1))
            this.updateReserves(res0, res1);
    }
    getOutput(amountIn, direction) {
        let inIndex = direction ? this.token0Index : this.token1Index;
        const outIndex = direction ? this.token1Index : this.token0Index;
        const inAmountBN = (0, util_1.getBigNumber)(amountIn);
        const normalizedBalances = this.metaSwap.xp;
        const baseLPTokenIndex = normalizedBalances.length - 1;
        let newInBalance = jsbi_1.default.BigInt(0);
        if (inIndex < baseLPTokenIndex) {
            newInBalance = jsbi_1.default.add(normalizedBalances[inIndex], jsbi_1.default.multiply(jsbi_1.default.BigInt(inAmountBN.toString()), this.metaSwap.tokenMultipliers[inIndex]));
        }
        else {
            inIndex = inIndex - baseLPTokenIndex;
            if (outIndex < baseLPTokenIndex) {
                const baseInputs = this.baseSwap.pooledTokens.map((token, index) => index === inIndex
                    ? currency_1.Amount.fromRawAmount(token, inAmountBN.toString())
                    : currency_1.Amount.fromRawAmount(token, 0));
                newInBalance = jsbi_1.default.divide(jsbi_1.default.multiply(jsbi_1.default.BigInt(this.baseSwap.calculateTokenAmount(baseInputs, true).quotient.toString()), this.baseSwap.virtualPriceRaw), BASE_VIRTUAL_PRICE_PRECISION);
                newInBalance = jsbi_1.default.add(newInBalance, normalizedBalances[baseLPTokenIndex]);
            }
            else {
                return { output: 0, gasSpent: this.swapGasCost };
            }
            inIndex = baseLPTokenIndex;
        }
        let metaIndexTo = baseLPTokenIndex;
        if (outIndex < baseLPTokenIndex)
            metaIndexTo = outIndex;
        const outBalance = this.metaSwap._getY(inIndex, metaIndexTo, newInBalance, normalizedBalances);
        const outAmount = jsbi_1.default.subtract(normalizedBalances[metaIndexTo], outBalance);
        if (outIndex < baseLPTokenIndex) {
            const amount = jsbi_1.default.divide(outAmount, this.metaSwap.tokenMultipliers[metaIndexTo]);
            const fee = jsbi_1.default.divide(jsbi_1.default.multiply(this.metaSwap.swapFee, amount), FEE_DENOMINATOR);
            return {
                output: Number.parseInt(jsbi_1.default.subtract(amount, fee).toString()),
                gasSpent: this.swapGasCost,
            };
        }
        else {
            const amount = this.baseSwap.calculateRemoveLiquidityOneToken(currency_1.Amount.fromRawAmount(this.baseSwap.liquidityToken, jsbi_1.default.divide(jsbi_1.default.multiply(outAmount, BASE_VIRTUAL_PRICE_PRECISION), this.baseSwap.virtualPriceRaw)), outIndex - baseLPTokenIndex)[0];
            const fee = jsbi_1.default.divide(jsbi_1.default.multiply(this.metaSwap.swapFee, jsbi_1.default.BigInt(amount.quotient.toString())), FEE_DENOMINATOR);
            return {
                output: Number.parseInt(jsbi_1.default.subtract(jsbi_1.default.BigInt(amount.quotient.toString()), fee).toString()),
                gasSpent: this.swapGasCost,
            };
        }
    }
    getInput(amountOut, direction) {
        const inIndex = direction ? this.token0Index : this.token1Index;
        let outIndex = direction ? this.token1Index : this.token0Index;
        const outAmountBN = (0, util_1.getBigNumber)(amountOut);
        const normalizedBalances = this.metaSwap.xp;
        const baseLPTokenIndex = normalizedBalances.length - 1;
        let newOutBalance = jsbi_1.default.BigInt(0);
        if (outIndex < baseLPTokenIndex) {
            newOutBalance = jsbi_1.default.subtract(normalizedBalances[outIndex], jsbi_1.default.multiply(jsbi_1.default.BigInt(outAmountBN.toString()), this.metaSwap.tokenMultipliers[outIndex]));
        }
        else {
            outIndex = outIndex - baseLPTokenIndex;
            if (inIndex < baseLPTokenIndex) {
                const baseInputs = this.baseSwap.pooledTokens.map((token, index) => index === outIndex
                    ? currency_1.Amount.fromRawAmount(token, outAmountBN.toString())
                    : currency_1.Amount.fromRawAmount(token, 0));
                newOutBalance = jsbi_1.default.divide(jsbi_1.default.multiply(jsbi_1.default.BigInt(this.baseSwap.calculateTokenAmount(baseInputs, true).quotient.toString()), this.baseSwap.virtualPriceRaw), BASE_VIRTUAL_PRICE_PRECISION);
                newOutBalance = jsbi_1.default.subtract(normalizedBalances[baseLPTokenIndex], newOutBalance);
            }
            else {
                return { input: 0, gasSpent: this.swapGasCost };
            }
            outIndex = baseLPTokenIndex;
        }
        let metaIndexFrom = baseLPTokenIndex;
        if (inIndex < baseLPTokenIndex)
            metaIndexFrom = inIndex;
        const inBalance = this.metaSwap._getY(outIndex, metaIndexFrom, newOutBalance, normalizedBalances);
        const inAmount = jsbi_1.default.subtract(inBalance, normalizedBalances[metaIndexFrom]);
        if (inIndex < baseLPTokenIndex) {
            const amount = jsbi_1.default.divide(inAmount, this.metaSwap.tokenMultipliers[metaIndexFrom]);
            const inAmountWithFee = jsbi_1.default.divide(amount, jsbi_1.default.subtract(math_1.ONE, jsbi_1.default.divide(this.metaSwap.swapFee, FEE_DENOMINATOR)));
            return {
                input: Number.parseInt(inAmountWithFee.toString()),
                gasSpent: this.swapGasCost,
            };
        }
        else {
            const amount = this.baseSwap.calculateRemoveLiquidityOneToken(currency_1.Amount.fromRawAmount(this.baseSwap.liquidityToken, jsbi_1.default.divide(jsbi_1.default.multiply(inAmount, BASE_VIRTUAL_PRICE_PRECISION), this.baseSwap.virtualPriceRaw)), inIndex - baseLPTokenIndex)[0];
            const inAmountWithFee = jsbi_1.default.divide(jsbi_1.default.BigInt(amount.quotient.toString()), jsbi_1.default.subtract(math_1.ONE, jsbi_1.default.divide(this.baseSwap.swapFee, FEE_DENOMINATOR)));
            return {
                input: Number.parseInt(inAmountWithFee.toString()),
                gasSpent: this.swapGasCost,
            };
        }
    }
    calcCurrentPriceWithoutFee(direction) {
        const inIndex = direction ? this.token0Index : this.token1Index;
        const baseLPTokenIndex = this.metaSwap.xp.length - 1;
        const inToken = inIndex < baseLPTokenIndex
            ? this.metaSwap.getToken(inIndex)
            : this.baseSwap.getToken(inIndex - baseLPTokenIndex);
        return this.calcPrice(10 ** inToken.decimals, direction);
    }
    calcPrice(amountIn, direction) {
        (0, tiny_invariant_1.default)(amountIn !== 0, 'MetaPool: Invalid zero amountIn');
        const { output } = this.getOutput(amountIn, direction);
        return output / amountIn;
    }
}
exports.MetaPool = MetaPool;
